/**
 * `number` and `vec3` widgets.
 *
 * Text is edited as a local draft while focused, so incomplete input like "-"
 * or "1." is allowed; each complete number applies live. Up/Down step by the
 * prop's step (Shift for ×10). Vec3 fields carry the theme's axis colors, as
 * in reference 02. A vec3 is mixed per axis: editing X on several entities
 * leaves each one's Y and Z as they were, as in Unreal.
 */
import { useState } from "react";
import type { PropType } from "../../world/schema/propTypes";
import { formatNumber } from "../format/numbers";
import { FieldError, fieldAria } from "./fieldAria";
import { isMixed, MIXED } from "./mixed";
import { commitOnEnter, mixedPlaceholder } from "./TextWidgets";
import type { WidgetProps } from "./types";

type NumberProp = Extract<PropType, { type: "number" }>;
type Vec3Prop = Extract<PropType, { type: "vec3" }>;

const AXES = ["x", "y", "z"] as const;

export function NumberWidget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const number = prop as NumberProp;
  return (
    <span className="kn-field">
      <span className="kn-number">
        <NumberInput
          id={id}
          label={label}
          value={isMixed(value) ? MIXED : typeof value === "number" ? value : 0}
          prop={number}
          onChange={onChange}
          onCommit={onCommit}
          error={error}
        />
        {number.unit && <span className="kn-number__unit">{number.unit}</span>}
      </span>
      <FieldError id={id} error={error} />
    </span>
  );
}

export function Vec3Widget({ id, label, prop, value, onChange, onCommit, error }: WidgetProps) {
  const vec = prop as Vec3Prop;
  const current: readonly AxisValue[] = isVec3(value) ? value : [0, 0, 0];

  return (
    <span className="kn-field">
      <span className="kn-vec3">
        {AXES.map((axis, i) => (
          <span key={axis} className="kn-vec3__axis" data-axis={axis}>
            <NumberInput
              id={i === 0 ? id : `${id}-${axis}`}
              label={`${label} ${axis.toUpperCase()}`}
              value={current[i] ?? 0}
              prop={vec}
              onChange={(n) => {
                const next = [...current];
                next[i] = n;
                onChange(next);
              }}
              onCommit={onCommit}
              error={error}
            />
          </span>
        ))}
      </span>
      <FieldError id={id} error={error} />
    </span>
  );
}

/** One axis or number: a value, or MIXED when the selected entities differ. */
type AxisValue = number | typeof MIXED;

interface NumberInputProps {
  readonly id: string;
  readonly label: string;
  readonly value: AxisValue;
  readonly prop: NumberProp | Vec3Prop;
  readonly onChange: (value: number) => void;
  readonly onCommit: () => void;
  readonly error: string | null | undefined;
}

/** A complete decimal number: "-" and "1." are drafts, not values. */
const COMPLETE_NUMBER = /^-?(\d+(\.\d+)?|\.\d+)$/;

function NumberInput({ id, label, value, prop, onChange, onCommit, error }: NumberInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  const step = prop.step ?? 1;
  const format = {
    ...(prop.step !== undefined && { step: prop.step }),
    ...(prop.unit !== undefined && { unit: prop.unit }),
  };

  const stepBy = (direction: 1 | -1, multiplier: number) => {
    // A mixed field has no one value to step from.
    if (isMixed(value)) return;
    const next = clamp(round(value + direction * step * multiplier), prop);
    setDraft(null);
    onChange(next);
  };

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      className="kn-input kn-input--number"
      aria-label={label}
      value={draft ?? (isMixed(value) ? "" : formatNumber(value, format))}
      {...mixedPlaceholder(value)}
      disabled={prop.readonly === true}
      {...fieldAria(id, error)}
      onChange={(event) => {
        const text = event.target.value;
        setDraft(text);
        if (COMPLETE_NUMBER.test(text.trim())) onChange(Number(text));
      }}
      onBlur={() => {
        setDraft(null);
        onCommit();
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          event.preventDefault();
          stepBy(event.key === "ArrowUp" ? 1 : -1, event.shiftKey ? 10 : 1);
          return;
        }
        if (event.key === "Enter") setDraft(null);
        commitOnEnter(event, onCommit);
      }}
    />
  );
}

/** Rounds away float noise from repeated stepping (2 + 0.1 → 2.1, not 2.1000000000000001). */
function round(value: number): number {
  return Number(value.toFixed(6));
}

function clamp(value: number, prop: NumberProp | Vec3Prop): number {
  if (prop.type !== "number") return value;
  return Math.min(prop.max ?? Infinity, Math.max(prop.min ?? -Infinity, value));
}

function isVec3(value: unknown): value is readonly AxisValue[] {
  return (
    Array.isArray(value) &&
    value.length === 3 &&
    value.every((n) => typeof n === "number" || isMixed(n))
  );
}
